import React, { useEffect } from "react";
import { createPortal } from "react-dom";

const ChatActionModal = ({
  isOpen,
  onClose,
  onConfirm,
  onDownloadAndConfirm,
  title,
  description,
  confirmButtonText,
  downloadButtonText,
  isLoading = false,
  loadingAction = null, // 'simple' or 'download'
}) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 z-10">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close modal"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Modal content */}
        <div className="p-6">
          {/* Title */}
          <h3 className="text-xl font-semibold text-[#1E1E1E] mb-2 pr-8">
            {title}
          </h3>

          {/* Description */}
          <p className="text-sm text-[#6C737F] mb-6">
            {description}
          </p>

          {/* Action buttons */}
          <div className="flex gap-3">
            {/* Secondary button - Simple Action */}
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-white text-[#1E1E1E] text-sm font-medium border border-[#E6E6E6] rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading && loadingAction === 'simple' ? (
                <>
                  <div className="w-4 h-4 border-2 border-[#2283FF] border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                confirmButtonText
              )}
            </button>

            {/* Primary button - Download & Action */}
            <button
              onClick={onDownloadAndConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-[#2283FF] text-white text-sm font-medium rounded-lg hover:bg-[#1a6acc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading && loadingAction === 'download' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  <span>{downloadButtonText}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ChatActionModal;
